/**
 * CloseOutProjectModal.jsx — "Close out project…" from a project's ⋯ menu. See
 * docs/PROJECT_CLOSEOUT.md. Mirrors PutProjectOnHoldModal.jsx's open/branch pattern: fetches the
 * project's closure status on open and branches between the "close it out" flow (Completed hard-
 * gated on zero open Threads + zero unposted milestones; Cancelled always requires a reason) and
 * an already-closed project's Reopen flow.
 *
 * Opened via `open-project-closeout-modal` ({ projId }); mounted once in OutlineView.
 */

import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ctx } from '../outline/ctx.js'
import {
  getProjectCloseoutStatus, getProjectCloseoutGateCheck, closeProject, reopenProject,
} from '../services/projectCloseout.js'
import { confirmDialog } from './Dialog.jsx'
import { useScrollLock } from '../hooks/useScrollLock.js'

export default function CloseOutProjectModal() {
  const [open, setOpen] = useState(false)
  const [projId, setProjId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [closure, setClosure] = useState(null)     // active closure row, or null
  const [gate, setGate] = useState(null)            // { openThreads, unpostedMilestones } | null while loading
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useScrollLock(open)

  useEffect(() => {
    function onOpen(e) {
      const id = e.detail && e.detail.projId
      setProjId(id)
      setReason(''); setError(''); setSaving(false)
      setClosure(null); setGate(null); setLoading(true)
      setOpen(true)
      getProjectCloseoutStatus(id).then(r => {
        const c = r && r.closure ? r.closure : null
        setClosure(c)
        setLoading(false)
        if (!c) getProjectCloseoutGateCheck(id).then(g => setGate(g)).catch(() => setGate({ openThreads: 0, unpostedMilestones: 0 }))
      }).catch(() => setLoading(false))
    }
    window.addEventListener('open-project-closeout-modal', onOpen)
    return () => window.removeEventListener('open-project-closeout-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) close() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  function close() { setOpen(false); setProjId(null) }

  // Checked both — this modal opens from EITHER the active list or the "Show closed projects"
  // list (Reopen), and the project only lives in whichever cache matches its current state.
  const proj = projId
    ? ((ctx.projectsCache || []).find(p => p.id === projId) || (ctx.closedProjectsCache || []).find(p => p.id === projId))
    : null
  const projName = proj ? (proj.fields['Project Name'] || 'This project') : 'This project'

  function removeFromCache(cacheKey, id) {
    if (Array.isArray(ctx[cacheKey])) ctx[cacheKey] = ctx[cacheKey].filter(p => p.id !== id)
  }
  function addToCache(cacheKey, record) {
    if (record && Array.isArray(ctx[cacheKey]) && !ctx[cacheKey].some(p => p.id === record.id)) {
      ctx[cacheKey] = [...ctx[cacheKey], record]
    }
  }

  const CONTRACT_STAGE_BY_STATUS = { completed: 'Completed', cancelled: 'Cancelled' }

  async function handleClose(status) {
    if (saving) return
    if (status === 'cancelled' && !reason.trim()) { setError('A reason is required to cancel a project.'); return }
    setSaving(true); setError('')
    try {
      const r = await closeProject(projId, { status, reason: reason.trim() || null })
      if (r.error) throw new Error(r.error)
      // Move the record out of the active list immediately — the row shouldn't wait for a full
      // reload to notice its own Contract Stage change. If the "Show closed projects" list is
      // already loaded, drop the updated record straight into it too.
      removeFromCache('projectsCache', projId)
      if (proj) addToCache('closedProjectsCache', { ...proj, fields: { ...proj.fields, 'Contract Stage': CONTRACT_STAGE_BY_STATUS[status] } })
      close()
      window.dispatchEvent(new CustomEvent('outline-render'))
    } catch (e) {
      setSaving(false)
      setError(e.message || 'Could not close out this project — please try again.')
    }
  }

  async function handleReopen() {
    if (saving) return
    const ok = await confirmDialog({
      title: 'Reopen project?',
      message: `${projName} will go back to active — billing, progress updates, and scheduling all resume as normal. Contractor Portal access disabled at closeout is NOT automatically restored.`,
      confirmLabel: 'Reopen project',
    })
    if (!ok) return
    setSaving(true); setError('')
    try {
      const r = await reopenProject(projId)
      if (r.error) throw new Error(r.error)
      const priorStage = (r.closure && r.closure.prior_contract_stage) || 'Executed'
      removeFromCache('closedProjectsCache', projId)
      // Only add back into the active list if the restored stage is one loadProjects() actually
      // includes (Executed or On Hold) — anything else is an edge case not worth showing a
      // phantom row for; it'll appear correctly on the next real fetch either way.
      if (proj && (priorStage === 'Executed' || priorStage === 'On Hold')) {
        addToCache('projectsCache', { ...proj, fields: { ...proj.fields, 'Contract Stage': priorStage } })
      }
      close()
      window.dispatchEvent(new CustomEvent('outline-render'))
    } catch (e) {
      setSaving(false)
      setError(e.message || 'Could not reopen this project — please try again.')
    }
  }

  if (!open) return null

  const label = { fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 6 }
  const input = { width: '100%', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', background: 'var(--aj-bg-page)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '8px 10px', boxSizing: 'border-box' }
  const btnBase = { border: 'none', borderRadius: 4, padding: '8px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 500, cursor: 'pointer' }
  const gateOk = gate && gate.openThreads === 0 && gate.unpostedMilestones === 0
    && gate.unpostedTime === 0 && gate.openArCount === 0 && !gate.openArUnknown
  const gateLoading = !closure && gate === null
  const money = n => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-modal-id="CloseOutProjectModal" style={{
        background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)',
        padding: '22px 24px', width: 440, maxWidth: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
      }}>
        <div style={{ fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 16 }}>
          Close out project
        </div>
        <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', padding: '8px 10px', background: 'var(--aj-bg-subtle)', borderRadius: 4, fontWeight: 500, marginBottom: 16 }}>
          {projName}
        </div>

        {loading ? (
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text-muted)', padding: '8px 0' }}>Checking status…</div>
        ) : closure ? (
          <>
            <p style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, lineHeight: 1.5, color: 'var(--aj-text)', margin: '0 0 12px' }}>
              This project was marked <strong>{closure.status === 'completed' ? 'Completed' : 'Cancelled'}</strong> on{' '}
              {new Date(closure.closed_at).toLocaleDateString()}. It's dropped out of billing, progress updates,
              scheduling, and the active project list, and any Contractor Portal access on it has been disabled.
            </p>
            {closure.reason && (
              <div style={{ ...input, background: 'var(--aj-bg-subtle)', marginBottom: 12, whiteSpace: 'pre-wrap' }}>{closure.reason}</div>
            )}
          </>
        ) : (
          <>
            <p style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, lineHeight: 1.5, color: 'var(--aj-text)', margin: '0 0 14px' }}>
              Marking this project Completed or Cancelled moves its Contract Stage — it drops out of billing,
              progress updates, scheduling, and the active project list (find it again from Projects ⋯ →
              Show closed projects), and any active Contractor Portal access on it is disabled.
            </p>

            <div style={{ marginBottom: 16 }}>
              <div style={label}>Complete</div>
              {gateLoading ? (
                <div style={{ fontSize: 12.5, color: 'var(--aj-text-muted)' }}>Checking readiness…</div>
              ) : gateOk ? (
                <div style={{ fontSize: 12.5, color: '#1a7a4a', marginBottom: 8 }}>✓ No open threads, every milestone posted, all time in Core, no outstanding AR — ready to complete.</div>
              ) : (
                <div style={{ fontSize: 12.5, color: '#b8540a', marginBottom: 8 }}>
                  Not ready:
                  {gate.openThreads > 0 && <> {gate.openThreads} open thread{gate.openThreads !== 1 ? 's' : ''} ·</>}
                  {gate.unpostedMilestones > 0 && <> {gate.unpostedMilestones} unposted milestone{gate.unpostedMilestones !== 1 ? 's' : ''} ·</>}
                  {gate.unpostedTime > 0 && <> {gate.unpostedTime} time entr{gate.unpostedTime !== 1 ? 'ies' : 'y'} not yet in Core ·</>}
                  {gate.openArUnknown && <> couldn't confirm AR status in Core ·</>}
                  {!gate.openArUnknown && gate.openArCount > 0 && <> {gate.openArCount} open invoice{gate.openArCount !== 1 ? 's' : ''} totaling {money(gate.openArTotal)} ·</>}
                  {' '}resolve these first.
                </div>
              )}
              <button
                onClick={() => handleClose('completed')} disabled={!gateOk || saving}
                style={{ ...btnBase, background: '#07272D', color: 'white', opacity: (!gateOk || saving) ? 0.4 : 1, cursor: (!gateOk || saving) ? 'not-allowed' : 'pointer' }}
              >
                {saving ? 'Completing…' : 'Mark Completed'}
              </button>
            </div>

            <div style={{ paddingTop: 14, borderTop: '0.5px solid var(--aj-border)' }}>
              <div style={label}>Cancel</div>
              <textarea
                style={{ ...input, minHeight: 56, resize: 'vertical', fontFamily: '"DM Sans",sans-serif', marginBottom: 8 }}
                placeholder="Reason (required) — e.g. client backed out, AJ declined to continue"
                value={reason} onChange={e => setReason(e.target.value)} disabled={saving}
              />
              <button
                onClick={() => handleClose('cancelled')} disabled={!reason.trim() || saving}
                style={{ ...btnBase, background: '#c0392b', color: 'white', opacity: (!reason.trim() || saving) ? 0.4 : 1, cursor: (!reason.trim() || saving) ? 'not-allowed' : 'pointer' }}
              >
                {saving ? 'Cancelling…' : 'Mark Cancelled'}
              </button>
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 20 }}>
          {error && <span style={{ fontSize: 12, color: '#c0392b', marginRight: 'auto' }}>{error}</span>}
          <button onClick={close} style={{ ...btnBase, background: 'transparent', border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text-mid)' }}>
            {closure ? 'Close' : 'Cancel'}
          </button>
          {!loading && closure && (
            <button onClick={handleReopen} disabled={saving} style={{ ...btnBase, background: '#07272D', color: 'white', opacity: saving ? 0.5 : 1 }}>
              {saving ? 'Reopening…' : 'Reopen project'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
